import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Clock, CheckCircle2, ArrowRight } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import EarlyAccessRequestDialog from "./EarlyAccessRequestDialog";

/**
 * Inline "Request Early Access" CTA rendered next to the marketing copy
 * on the landing page's Beta Access & Feedback section.
 *
 * State-aware — uses the same ``/private-tester-requests/me`` state as
 * the Dashboard access card + limited banner, so the three surfaces
 * never disagree:
 *
 *   * Unauthenticated visitor → link to /login (existing signup path
 *     unchanged; landing copy already invites sign-in).
 *   * Signed-in, non-approved, no request → button that opens the same
 *     shared ``EarlyAccessRequestDialog``.
 *   * Signed-in with a pending request → readable "Request Pending"
 *     pill (matches the Dashboard/Profile/Jobs pill styling).
 *   * Signed-in Early Access active → "Early Access Active" badge, no
 *     request button.
 *   * Admins & ephemeral demo visitors → nothing (they don't need it).
 */
export default function EarlyAccessInlineCTA() {
  const { user } = useAuth();
  const [state, setState] = useState({ loading: true, status: "none" });
  const [dialogOpen, setDialogOpen] = useState(false);

  const isAdmin = (user?.roles || []).includes("admin");
  const isAlreadyPT = !!user?.is_private_test;
  const isVerified = !!user?.email_verified;
  const isSignedIn = !!user && user !== false;

  useEffect(() => {
    if (!isSignedIn || isAdmin || isAlreadyPT || user?.is_ephemeral) {
      setState({ loading: false, status: isAlreadyPT ? "approved" : "none" });
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/private-tester-requests/me");
        if (!cancelled) setState({ loading: false, status: data.status });
      } catch {
        if (!cancelled) setState({ loading: false, status: "none" });
      }
    })();
    return () => { cancelled = true; };
  }, [isSignedIn, isAdmin, isAlreadyPT, user]);

  // Admins and ephemeral demo visitors don't see any CTA on this section.
  if (isAdmin || user?.is_ephemeral) return null;

  // Unauthenticated → sign-in link (keeps existing landing signup funnel).
  if (!isSignedIn) {
    return (
      <div className="mt-3">
        <Link
          to="/login"
          className="btn-primary inline-flex whitespace-nowrap"
          data-testid="beta-cta-signin-to-request"
        >
          Sign in to request Early Access <ArrowRight size={14} />
        </Link>
      </div>
    );
  }

  if (state.loading) return null;

  // Already approved — celebrate, no request button.
  if (isAlreadyPT || state.status === "approved") {
    return (
      <div className="mt-3">
        <span
          className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold bg-[hsl(var(--sb-forest))] text-[hsl(42_60%_96%)] border border-[hsl(var(--sb-forest))] shadow-sm"
          data-testid="beta-cta-active-pill"
        >
          <CheckCircle2 size={14} /> Early Access Active
        </span>
      </div>
    );
  }

  // Pending request → same high-contrast pill used elsewhere.
  if (state.status === "pending") {
    return (
      <div className="mt-3">
        <span
          className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold bg-[hsl(var(--sb-gold-2))] text-[hsl(150_28%_10%)] border border-[hsl(var(--sb-gold-2))] shadow-sm"
          data-testid="beta-cta-pending-pill"
          aria-disabled="true"
        >
          <Clock size={14} /> Request Pending
        </span>
      </div>
    );
  }

  // Signed-in, eligible, no active request → primary CTA.
  const onRequest = () => {
    if (!isVerified) {
      toast.error("Please verify your email before requesting Early Access.");
      return;
    }
    setDialogOpen(true);
  };

  return (
    <div className="mt-3">
      <button
        type="button"
        onClick={onRequest}
        className="btn-primary whitespace-nowrap"
        data-testid="beta-cta-request-button"
      >
        Request Early Access
      </button>
      {dialogOpen && (
        <EarlyAccessRequestDialog
          onClose={() => setDialogOpen(false)}
          onSubmitted={() => {
            setState({ loading: false, status: "pending" });
            setDialogOpen(false);
          }}
        />
      )}
    </div>
  );
}
